import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import {
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  Info,
  X,
} from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Figma (node 21:986):
 * Container: bg-card, border border-border, rounded-lg (10px), px-2.5 (10px), py-2 (8px)
 * Layout: flex gap-2 items-center (horizontal)
 * Title: text-sm font-medium leading-5 text-foreground, overflow-hidden text-ellipsis whitespace-nowrap
 * Description: text-sm font-normal leading-5 text-muted-foreground
 * Title+Desc gap: gap-0.5 (2px)
 * Icon: 16px with pt-[2px]
 * Variants: Default, Destructive
 */
const alertVariants = cva(
  'relative w-full rounded-lg border px-2.5 py-2 text-sm',
  {
    variants: {
      variant: {
        default: 'bg-card border-border text-foreground',
        destructive:
          'border-destructive/50 text-destructive dark:border-destructive [&>div>div>svg]:text-destructive',
        success:
          'border-green-500/50 text-green-700 dark:text-green-400 [&>div>div>svg]:text-green-600',
        warning:
          'border-yellow-500/50 text-yellow-700 dark:text-yellow-400 [&>div>div>svg]:text-yellow-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

type AlertVariant = 'default' | 'destructive' | 'success' | 'warning'

const variantIcons: Record<AlertVariant, React.ElementType> = {
  default: Info,
  destructive: AlertCircle,
  success: CheckCircle2,
  warning: AlertTriangle,
}

export interface IntuiAlertProps extends VariantProps<typeof alertVariants> {
  title: string
  description?: ReactNode
  icon?: ReactNode
  dismissible?: boolean
  onDismiss?: () => void
  className?: string
  action?: ReactNode
}

function IntuiAlert({
  variant = 'default',
  title,
  description,
  icon,
  dismissible,
  onDismiss,
  className,
  action,
}: IntuiAlertProps) {
  const IconComponent = variantIcons[variant as AlertVariant] ?? Info

  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)}>
      {/* Figma: flex gap-2 items-center horizontal layout */}
      <div className="flex gap-2 items-start">
        {/* Icon — Figma: 16px with pt-[2px] */}
        <div className="flex items-start pt-[2px] shrink-0">
          {icon ?? <IconComponent className="h-4 w-4" />}
        </div>
        {/* Content — Figma: flex-1 flex-col gap-0.5 */}
        <div className="flex flex-1 flex-col gap-0.5 min-w-0 leading-5">
          <p className="font-medium overflow-hidden text-ellipsis whitespace-nowrap">
            {title}
          </p>
          {description && (
            <div className="font-normal text-muted-foreground">{description}</div>
          )}
        </div>
        {/* Dismiss icon — Figma: X icon 16px, pt-[2px] */}
        {dismissible && (
          <button
            onClick={onDismiss}
            className="flex items-start pt-[2px] shrink-0 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Dismiss alert"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export { IntuiAlert, alertVariants }
